import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BookOpen, FileText, CheckCircle2 } from "lucide-react";

import WeekDropdown from "../../logbook/components/WeekDropdown";
import WeekCalendar from "../../logbook/components/WeekCalendar";
import StatusBadge from "../../logbook/components/StatusBadge";
import LogbookAnalysisSheet from "../../logbook/components/LogbookAnalysisSheet";
import LogbookAnalysisGraphs from "../../logbook/components/LogbookAnalysisGraphs";

import {
  useGetSupervisorLogbookDetailsQuery,
  useGetLogbookWeekEntriesQuery,
} from "../../logbook/api/logbookApi";
import { useAssessAssessmentLogbookMutation } from "../../logbook/api/logbookAssessmentDietApi";

import { STORAGE_KEY } from "../../../features/api/apiSlice";
import { describeApiError } from "../../../lib/apiError";

import {
  Day,
  DAYS,
  NUM_TO_DAY,
  safeDate,
  fullNameFromSupervisor,
  computeStatus,
} from "../../logbook/utils/logbook";

// The four weighted grading criteria; the overall score is their sum (max 100).
const GRADING_CRITERIA = [
  { key: "quality_of_entries", label: "Quality of entries", max: 30 },
  { key: "competency_coverage", label: "Level of coverage of QS competency area", max: 30 },
  { key: "originality_reality", label: "Originality and reality of entries", max: 15 },
  { key: "report_writing", label: "Report writing", max: 25 },
] as const;

type CriteriaKey = (typeof GRADING_CRITERIA)[number]["key"];

function safeMsg(x: any) {
  return String(
    x?.data?.message ||
      x?.message ||
      x?.error ||
      x?.data?.error ||
      "Something went wrong"
  );
}

function getAuth(): any {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function AssessorLogbookDetails() {
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // When reached from the new assessment-diet flow, the diet id is passed as
  // ?diet=... so grading is routed through the diet-scoped assess endpoint.
  const dietIdFromUrl = searchParams.get("diet") || "";

  // ✅ Logged in user's member_id (adjust paths based on what you store)
  const auth = useMemo(() => getAuth(), []);
  const myMemberId = auth?.user?.member?.id ?? auth?.member_id ?? null;

  const {
    data: logbook,
    isLoading,
    error,
    refetch,
  } = useGetSupervisorLogbookDetailsQuery(
    { logbook_id: id || "" },
    { skip: !id }
  );

  const [selectedWeek, setSelectedWeek] = useState(1);

  const status = useMemo(() => {
    if (!logbook) return "none";
    return computeStatus(logbook);
  }, [logbook]);

  // ✅ load week entries only when approved
  // `currentData`, NOT `data` — see LogbookDetails. `data` survives a change of
  // week, so any week the server refuses (anything past `position + 1`) kept
  // showing the last week that loaded, relabelled.
  const {
    currentData: weekData,
    isFetching: weekLoading,
    refetch: refetchWeek,
  } = useGetLogbookWeekEntriesQuery(
    { logbook_id: id, week: selectedWeek },
    {
      skip: !id || !logbook || status !== "approved",
      refetchOnMountOrArgChange: true,
    }
  );

  const weeksWithEntries = useMemo(() => {
    if (!weekData?.entries?.length) return new Set<number>();
    return new Set([selectedWeek]);
  }, [weekData, selectedWeek]);

  const entriesByDay = useMemo(() => {
    const map: Record<string, any> = {};
    (weekData?.entries || []).forEach((e: any) => {
      const day = NUM_TO_DAY[e.day];
      if (day) map[day] = e;
    });
    return map;
  }, [weekData]);

  // =========================
  // ✅ GRADING (Slide-over)
  // =========================
  const [assessDietLogbook, { isLoading: gradingDiet }] =
    useAssessAssessmentLogbookMutation();
  const busy = gradingDiet;
  const [gradeErr, setGradeErr] = useState<string | null>(null);
  const [gradeOk, setGradeOk] = useState<string | null>(null);
  const [gradeOpen, setGradeOpen] = useState(false);

  const [grade, setGrade] = useState({
    quality_of_entries: 0,
    competency_coverage: 0,
    originality_reality: 0,
    report_writing: 0,
    comment: "",
    result: "pass" as "pass" | "fail",
    repeat_type: "full" as "full" | "half",
  });

  // ✅ compare logged-in member_id with logbook.assessor_id
  // (cast to string to avoid UUID vs number issues)
  const isMyAssignedLogbook = useMemo(() => {
    if (!logbook || !myMemberId) return false;
    return String(logbook.assessor_id) === String(myMemberId);
  }, [logbook, myMemberId]);

  // ✅ only grade submitted + not already assessed + not graded
  const canGrade = useMemo(() => {
    if (!logbook) return false;

    const alreadyAssessed = Boolean(logbook.assessed_by);
    const s = String(logbook.status || "").toLowerCase();

    const isSubmitted = s === "submitted" || s === "awaiting_assessment";
    const isGraded = s === "graded";

    return isMyAssignedLogbook && isSubmitted && !alreadyAssessed && !isGraded;
  }, [logbook, isMyAssignedLogbook]);

  // Overall score = sum of the four weighted criteria (max 100).
  const overallScore = useMemo(
    () =>
      GRADING_CRITERIA.reduce(
        (acc, c) => acc + Number((grade as any)[c.key] || 0),
        0
      ),
    [grade]
  );

  // The three outcomes the assessor can pick.
  const outcome: "pass" | "fail_full" | "fail_half" =
    grade.result === "pass"
      ? "pass"
      : grade.repeat_type === "half"
      ? "fail_half"
      : "fail_full";

  const setOutcome = (o: "pass" | "fail_full" | "fail_half") =>
    setGrade((p) => ({
      ...p,
      result: o === "pass" ? "pass" : "fail",
      repeat_type: o === "fail_half" ? "half" : "full",
    }));

  const resetGrade = () => {
    setGradeErr(null);
    setGradeOk(null);
    setGrade({
      quality_of_entries: 0,
      competency_coverage: 0,
      originality_reality: 0,
      report_writing: 0,
      comment: "",
      result: "pass",
      repeat_type: "full",
    });
  };

  const submitGrade = async () => {
    if (!id) return;
    setGradeErr(null);
    setGradeOk(null);

    // Which assessment diet does this logbook belong to? (URL wins, else the
    // logbook's own stamp.) Grading is recorded against the diet.
    const dietId =
      dietIdFromUrl || (logbook as any)?.logbook_assessment_diet_id || "";
    if (!dietId) {
      setGradeErr("This logbook is not part of an assessment diet.");
      return;
    }

    try {
      const repeat_type = grade.result === "fail" ? grade.repeat_type : undefined;

      const res = await assessDietLogbook({
        id: dietId,
        logbookId: id,
        quality_of_entries: Number(grade.quality_of_entries),
        competency_coverage: Number(grade.competency_coverage),
        originality_reality: Number(grade.originality_reality),
        report_writing: Number(grade.report_writing),
        comment: grade.comment || undefined,
        result: grade.result,
        ...(repeat_type ? { repeat_type } : {}),
      }).unwrap();

      setGradeOk(res?.message || "Assessment completed successfully");

      await refetch();
      setGradeOpen(false);
    } catch (e: any) {
      setGradeErr(safeMsg(e));
    }
  };

  // ✅ show grade trigger ONLY if:
  // - logbook not graded yet
  // - AND logged-in member_id matches logbook.assessor_id
  const showGradeTrigger = useMemo(() => {
    if (!logbook) return false;
    const s = String(logbook.status || "").toLowerCase();
    return isMyAssignedLogbook && s !== "graded";
  }, [logbook, isMyAssignedLogbook]);

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {/* Top bar */}
      <div className="mb-4 flex items-center justify-between">
        <button
          className="text-xs border px-3 py-1 rounded bg-white"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>

        <div className="flex gap-2">
          <button
            className="text-xs border px-3 py-1 rounded bg-white"
            onClick={() => refetch()}
          >
            Refresh
          </button>
          <button
            className="text-xs border px-3 py-1 rounded bg-white"
            onClick={() => refetchWeek()}
            disabled={!id || status !== "approved"}
          >
            Refresh Week
          </button>
        </div>
      </div>

      {isLoading && <p className="text-sm">Loading...</p>}

      {error && (
        <div className="text-sm text-red-600 space-y-2">
          <p>{describeApiError(error, "Failed loading Logbook")}</p>
        </div>
      )}

      {!isLoading && !logbook && (
        <div className="bg-white p-6 rounded-xl border">
          <p className="text-sm text-gray-700">Logbook not found.</p>
        </div>
      )}

      {logbook && (
        <div className="flex gap-6">
          {/* LEFT */}
          <div className="w-80 space-y-4">
            <div className="bg-indigo-50 p-5 rounded-xl border">
              <BookOpen size={24} className="text-indigo-600" />
              <h2 className="text-2xl font-black mt-2">
                Stage {logbook.stage || 1}
              </h2>

              <div className="mt-2">
                <StatusBadge status={logbook.status} />
              </div>

              <div className="mt-3 text-xs text-gray-700">
                <div className="font-semibold">{logbook?.diet?.title}</div>
                <div>
                  {safeDate(logbook?.diet?.start_date)} →{" "}
                  {safeDate(logbook?.diet?.end_date)}
                </div>
              </div>

              <div className="mt-3 text-xs text-gray-700">
                Progress:{" "}
                <span className="font-semibold">
                  {Number(logbook.position || 0)} / {Number(logbook.size || 0)}
                </span>
              </div>
            </div>

            {/* Assessment outcome — shown once graded */}
            {(() => {
              const a = Array.isArray(logbook?.assessments) ? logbook.assessments[0] : null;
              const result = String(a?.result ?? "").toLowerCase();
              const graded =
                Boolean(logbook?.graded_at) ||
                String(logbook?.status || "").toLowerCase() === "graded";
              if (!graded && !result) return null;
              const pass = result === "pass";
              return (
                <div
                  className={`p-5 rounded-xl border ${
                    pass
                      ? "bg-emerald-50 border-emerald-200"
                      : result === "fail"
                      ? "bg-red-50 border-red-200"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                    Assessment outcome
                  </div>
                  {result ? (
                    <div
                      className={`text-2xl font-black mt-1 capitalize ${
                        pass ? "text-emerald-700" : "text-red-700"
                      }`}
                    >
                      {result}
                    </div>
                  ) : (
                    <div className="text-sm font-semibold text-gray-600 mt-1">Graded</div>
                  )}
                  <div className="text-xs text-gray-600 mt-1">
                    Score:{" "}
                    <span className="font-semibold">
                      {logbook?.total_score != null ? `${logbook.total_score}/100` : "—"}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Logbook Report (uploaded by the probationer) */}
            <div className="bg-white p-4 rounded-xl border">
              <div className="flex items-center gap-2 mb-2">
                <FileText size={16} className="text-indigo-600" />
                <h4 className="font-bold text-sm">Logbook Report</h4>
              </div>
              {logbook?.report_url ? (
                <a
                  href={logbook.report_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:underline p-2 rounded-lg bg-emerald-50 border border-emerald-100"
                >
                  <CheckCircle2 size={14} className="shrink-0" />
                  <span className="truncate">
                    View report{logbook.report_pages ? ` (${logbook.report_pages} pages)` : ""}
                  </span>
                </a>
              ) : (
                <p className="text-[11px] text-gray-400 italic">
                  No report uploaded by the probationer yet.
                </p>
              )}
            </div>

            <div className="bg-white p-4 rounded-xl border">
              <h4 className="font-bold text-sm mb-1">Supervisor</h4>
              <p className="text-sm text-gray-800">
                {fullNameFromSupervisor(logbook.supervisor)}
              </p>

              {status !== "approved" && (
                <p className="text-xs text-gray-500 mt-2">
                  Entries are visible only when supervisor has approved.
                </p>
              )}
            </div>

            {/* ✅ week navigation only */}
            {status === "approved" && (
              <div className="bg-white p-4 rounded-xl border">
                <h4 className="font-bold text-sm">Select Week</h4>
                <WeekDropdown
                  value={selectedWeek}
                  onChange={setSelectedWeek}
                  weeks={logbook.size}
                />
                {weekLoading && (
                  <p className="text-xs text-gray-500 mt-2">Loading week…</p>
                )}
              </div>
            )}

            {status === "approved" && (
              <WeekCalendar
                totalWeeks={Number(logbook.size || 0)}
                value={selectedWeek}
                onChange={setSelectedWeek}
                loading={weekLoading}
                weeksWithEntries={weeksWithEntries as any}
              />
            )}
          </div>

          {/* RIGHT */}
          <div className="flex-1 space-y-6">
            {/* Application */}
            <div className="bg-white p-5 rounded-xl border">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold mb-1">Application</h3>
                  <p className="text-xs text-gray-500">
                    View applicant details and weekly entries.
                  </p>
                </div>

                {/* ✅ Grade trigger button (STRICT visibility) */}
                {showGradeTrigger && (
                  <button
                    type="button"
                    onClick={() => setGradeOpen(true)}
                    disabled={!canGrade}
                    className="text-xs px-3 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60"
                    title={
                      canGrade
                        ? "Open grading panel"
                        : "Logbook must be submitted and not previously assessed"
                    }
                  >
                    Grade Logbook
                  </button>
                )}
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500">Name</div>
                  <div className="font-medium">
                    {logbook?.application?.title}{" "}
                    {logbook?.application?.surname}{" "}
                    {logbook?.application?.other_names}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500">Email</div>
                  <div className="font-medium">
                    {logbook?.application?.email || "—"}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500">Phone</div>
                  <div className="font-medium">
                    {logbook?.application?.phone || "—"}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500">Status</div>
                  <div className="font-medium">
                    {logbook?.application?.status || "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* ✅ Analysis sheet + graphs — subjects × weeks coverage */}
            {id && (
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <LogbookAnalysisSheet logbookId={id} />
                </div>
                <div className="flex-1">
                  <LogbookAnalysisGraphs logbookId={id} />
                </div>
              </div>
            )}

            {/* ✅ READ-ONLY week entries */}
            {status === "approved" ? (
              <div className="space-y-4">
                <div className="flex items-end justify-between">
                  <div>
                    <h3 className="text-sm font-bold">
                      Week {selectedWeek} Entries
                    </h3>
                  </div>
                  {weekLoading && (
                    <p className="text-xs text-gray-500">Loading…</p>
                  )}
                </div>

                {DAYS.map((day: Day) => {
                  const entry = entriesByDay[day];

                  return (
                    <div
                      key={day}
                      className="bg-white p-4 rounded-xl border space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{day}</p>
                        {entry?.status && <StatusBadge status={entry.status} />}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div>
                          <div className="text-xs text-gray-500">
                            Entry Date
                          </div>
                          <div className="font-medium">
                            {safeDate(entry?.entry_date) || "—"}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-500">Hours</div>
                          <div className="font-medium">
                            {entry?.hours ?? "—"}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-500">
                            Last Updated
                          </div>
                          <div className="font-medium">
                            {safeDate(entry?.updated_at) || "—"}
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="text-xs text-gray-500 mt-2">
                          Subject / Training Code
                        </div>
                        <div className="font-medium text-sm text-gray-800">
                          {entry?.training_code
                            ? `${entry.training_code.code} — ${entry.training_code.description}`
                            : entry?.subject != null
                            ? String(entry.subject)
                            : "—"}
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 mt-2">Activity</div>
                      <div className="text-sm text-gray-800 whitespace-pre-wrap border rounded p-3 bg-gray-50">
                        {entry?.activity || "—"}
                      </div>

                      {entry?.supervisor_comment ? (
                        <div className="mt-2 rounded bg-gray-50 border p-3 text-xs">
                          <p className="font-semibold text-gray-700">
                            Supervisor Feedback
                          </p>
                          <p className="mt-1 text-gray-600 whitespace-pre-wrap">
                            {entry.supervisor_comment}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white p-6 rounded-xl border">
                <p className="text-sm text-gray-700">
                  This logbook is not approved yet, so week entries are not
                  available.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ✅ Slide-over grading panel */}
      {gradeOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setGradeOpen(false)}
          />

          <div className="absolute right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl border-l flex flex-col">
            <div className="p-5 border-b flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold">Grade Logbook</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Score each criterion within its weight. Overall (total):{" "}
                  <span className="font-semibold text-indigo-700">
                    {overallScore}
                  </span>
                  /100
                </p>
                {!canGrade && (
                  <p className="text-xs text-yellow-700 mt-2">
                    You can only grade when it is <b>submitted</b>, not
                    assessed, and assigned to you.
                  </p>
                )}
              </div>

              <button
                className="text-xs border px-3 py-1 rounded bg-white hover:bg-gray-50"
                onClick={() => setGradeOpen(false)}
                disabled={busy}
              >
                Close
              </button>
            </div>

            <div className="p-5 flex-1 overflow-auto space-y-4">
              {gradeErr && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded p-3">
                  {gradeErr}
                </div>
              )}

              {gradeOk && (
                <div className="text-xs text-green-700 bg-green-50 border border-green-100 rounded p-3">
                  {gradeOk}
                </div>
              )}

              {/* Grading criteria — each scored within its own weight */}
              <div className="space-y-3">
                {GRADING_CRITERIA.map((c) => {
                  const val = Number((grade as any)[c.key] || 0);
                  const pct = c.max > 0 ? (val / c.max) * 100 : 0;
                  return (
                    <div
                      key={c.key}
                      className="rounded-xl border p-3 bg-gray-50/60"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <label className="text-xs font-semibold text-gray-700">
                          {c.label}
                        </label>
                        <div className="flex items-center gap-1 shrink-0">
                          <input
                            type="number"
                            min={0}
                            max={c.max}
                            value={(grade as any)[c.key]}
                            disabled={!canGrade || busy}
                            onChange={(e) =>
                              setGrade((p) => ({
                                ...p,
                                [c.key as CriteriaKey]: Math.max(
                                  0,
                                  Math.min(c.max, Number(e.target.value))
                                ),
                              }))
                            }
                            className="w-16 border rounded-lg px-2 py-1.5 text-sm text-center disabled:bg-gray-100"
                          />
                          <span className="text-xs text-gray-400">/{c.max}</span>
                        </div>
                      </div>
                      {/* Progress bar (share of this criterion's weight) */}
                      <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-indigo-500 transition-all"
                          style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Outcome — pass / fail (repeat 52 wks) / fail (repeat 26 wks) */}
              <div>
                <label className="text-xs font-semibold text-gray-700">
                  Outcome
                </label>
                <div className="mt-2 grid grid-cols-1 gap-2">
                  {(
                    [
                      ["pass", "Pass", "Candidate passes this logbook"],
                      [
                        "fail_full",
                        "Fail — repeat logbook (52 weeks)",
                        "Repeat the full stage",
                      ],
                      [
                        "fail_half",
                        "Fail — repeat 26 weeks",
                        "Repeat half the stage",
                      ],
                    ] as const
                  ).map(([val, label, hint]) => {
                    const active = outcome === val;
                    const isPass = val === "pass";
                    return (
                      <button
                        key={val}
                        type="button"
                        disabled={!canGrade || busy}
                        onClick={() => setOutcome(val)}
                        className={`text-left rounded-xl border px-3 py-2.5 transition disabled:opacity-60 ${
                          active
                            ? isPass
                              ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-400"
                              : "border-red-400 bg-red-50 ring-1 ring-red-300"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <div
                          className={`text-sm font-semibold ${
                            active
                              ? isPass
                                ? "text-emerald-700"
                                : "text-red-700"
                              : "text-gray-800"
                          }`}
                        >
                          {label}
                        </div>
                        <div className="text-[11px] text-gray-500">{hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-600">
                  Comment (optional)
                </label>
                <textarea
                  value={grade.comment}
                  disabled={!canGrade || busy}
                  onChange={(e) =>
                    setGrade((p) => ({ ...p, comment: e.target.value }))
                  }
                  rows={4}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
                />
              </div>
            </div>

            <div className="p-5 border-t flex items-center justify-between">
              <button
                type="button"
                onClick={resetGrade}
                className="text-xs px-3 py-2 rounded border bg-white hover:bg-gray-50"
                disabled={busy}
              >
                Reset
              </button>

              <button
                type="button"
                onClick={submitGrade}
                disabled={!canGrade || busy}
                className="text-xs px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60"
              >
                {busy ? "Submitting..." : "Submit Grade"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
